import { Badge, Box, Chip, Stack, Typography, Tooltip, IconButton } from '@mui/material';
import { fCurrency, fCurrencyByCode } from './formatNumber';
import { fDate, fDateTime } from './formatTime';
import { ReactElement, useState } from 'react';
import Label from 'src/components/label';
import { STATUSES, SYNC_STATUSES } from 'src/constants/statuses';
import { useI18n, useAuth, t } from 'iomart-hooks';
import { statusValueToI18nPhrase } from 'src/utils/statusI18n';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'notistack';
import { getPrivateFileBlob, listPrivateFiles } from 'src/utils/privateFiles';
import navConfig from 'src/layouts/dashboard/nav/config-navigation';
import { ROLES } from 'src/constants/roles';
import { useTheme } from '@mui/material/styles';
import { formatOzonPostingStatusLabel, getOzonPostingStatusSxColor } from 'src/utils/ozonPostingStatus';
import { formatAvitoPostingStatusLabel, getAvitoPostingStatusLabelColor } from 'src/utils/avitoPostingStatus';


const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const BarcodeAssetChip: React.FC<{ productId: number }> = ({ productId }) => {
  const { t } = useI18n(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = async () => {
    try {
      const files = await listPrivateFiles('product', productId);
      const barcodeFile = files.find((f) => f.fileKey === 'barcode');
      const filename = barcodeFile?.originalName || 'barcode.nlbl';
      const blob = await getPrivateFileBlob('product', productId, 'barcode');
      downloadBlob(blob, filename);
    } catch (err: any) {
      enqueueSnackbar(err?.message || t('Failed to download barcode file'), { variant: 'error' });
    }
  };

  return (
    <Tooltip title={t('Download barcode file')}>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{ ml: 0.3, p: 0.25 }}
      >
        <Iconify icon="eva:download-fill" width={16} />
      </IconButton>
    </Tooltip>
  );
};

export const notNull = (formData: Record<string, any>): Record<string, any> =>
  Object.fromEntries(Object.entries(formData).filter(([key, value]) => value !== null));

/** Значение по пути вида `a.b.c`; без точки — как `obj[path]`. */
export const getValueByPath = (obj: any, path: string) => {
  if (obj == null || path == null || path === '') return undefined;
  if (!path.includes('.')) return obj[path];
  let current = obj;
  for (const key of path.split('.')) {
    if (current == null || current[key] === undefined) return undefined;
    current = current[key];
  }
  return current;
};

const extractTextInParentheses = (text: string | null | undefined) => {
  if (text == null || typeof text !== 'string') return '';
  const match = text.match(/\(([^)]+)\)/);
  return match ? match[1] : '';
};

export type Formats =
  | keyof typeof formats
  | ((item: any, name: string, params?: { [key: string]: any }) => string | ReactElement);
const formatPhone = number => {
  // Удаляем все символы, кроме цифр
  const digits = number.replace(/\D/g, '');

  // Извлекаем компоненты номера
  const code = digits.slice(1, 4); // код оператора
  const part1 = digits.slice(4, 7); // первые три цифры
  const part2 = digits.slice(7, 9); // следующие две цифры
  const part3 = digits.slice(9, 11); // последние две цифры

  // Форматируем и возвращаем номер
  return `+7 (${code}) ${part1}-${part2}-${part3}`;
};

export const formats = {
  emptyNullPecent: (item: any, name: string) => {
    if (item[name] && item[name] !== 0) {
      return `${item[name]}%`;
    }

    return '-';
  },
  phone: (item: any, name: string) => {
    if (item[name]) {
      return formatPhone(item[name]);
    }

    return '-';
  },
  selector: (item: any, name: string, params?: { selector?: (data: Record<string, any>) => string }) => {
    if (params?.selector) {
      return params.selector(item[name]);
    }

    return item[name];
  },
  linkedAmount: (item: any, name: string) => {
    const moveIn = item?.accountIn_id;
    const moveOut = item?.accountOut_id;
    const value = fCurrency(item[name], item.currency || 'RUB');

    if (!item[name]) {
      return undefined;
    }

    if (!moveIn) {
      return (
        <Box display="flex" alignItems="center">
          <Iconify icon="majesticons:money-minus-line" sx={{ mr: 1, color: 'error.main' }} />
          {value}
        </Box>
      );
    }

    if (!moveOut) {
      return (
        <Box display="flex" alignItems="center">
          <Iconify icon="majesticons:money-plus-line" sx={{ mr: 1, color: 'success.main' }} />
          {value}
        </Box>
      );
    }

    return (
      <Box display="flex" alignItems="center">
        <Iconify icon="eva:repeat-fill" sx={{ mr: 1, color: 'warning.main' }} />
        <Box display="flex" flexDirection="column" alignItems="start">
          <div>{value}</div>
        </Box>
      </Box>
    );
  },
  linkedComment: (item: any, name: string, params?: { digits?: number; nullable?: boolean }) => {
    const value = extractTextInParentheses(item[name]);

    return value;
  },
  money: (
    item: any,
    name: string,
    params?: { digits?: number; nullable?: boolean; currency?: 'RUB' | 'USD' | 'EUR' | 'CNY' }
  ) => {
    const Component = () => {
      const { mainCurrency } = useAuth(false);
      const cur = (params?.currency ?? item?.currency ?? mainCurrency ?? 'RUB') as
        | 'RUB'
        | 'USD'
        | 'EUR'
        | 'CNY';
      const value = fCurrency(item?.[name], cur, params?.digits);
      if (params?.nullable === false && !item?.[name]) {
        return null as any;
      }
      return <>{value || '-'}</>;
    };
    return <Component />;
  },
  moneyRUB: (
    item: any,
    name: string,
    params?: { digits?: number; nullable?: boolean; hudeNull?: boolean; callback?: (value, item) => any }
  ) => {
    const Component = () => {
      const { mainCurrency } = useAuth(false);
      const base = mainCurrency || 'RUB';
      const value = fCurrency(item[name], base, params?.digits);
      if (params?.nullable === false && !item[name]) {
        return null as any;
      }
      const result = (value as any) || '-';
      return <>{params?.callback ? params.callback(result, item) : result}</>;
    };
    return <Component />;
  },
  moneyWithRUB: (item: any, name: string) => {
    const Component = () => {
      const { mainCurrency } = useAuth(false);
      const base = mainCurrency || 'RUB';
      const primaryCurrency = item?.currency || base;
      const primary = fCurrency(item[name], primaryCurrency);
      const showSecond = primaryCurrency !== base;
      const second = showSecond ? ` (${fCurrency(item[`${name}Rub`], base)})` : '';
      return <>{`${primary}${second}`}</>;
    };
    return <Component />;
  },
  date: (item: any, name: string) => fDate(item[name]),
  dateTime: (item: any, name: string) => fDateTime(item[name]),
  time: (item: any, name: string) => fDateTime(item[name], 'p'),
  currency: (item: any, name: string) => {
    const Component = () => {
      const { mainCurrency } = useAuth(false);
      const base = mainCurrency || 'RUB';
      const curr = item?.currency || base;
      const showBase = curr !== base;
      return (
        <>
          {curr}
          {showBase ? ` (${fCurrency(item[name], base)})` : ''}
        </>
      );
    };
    return <Component />;
  },
  /** Показывает только mainCurrency (для документов, где валюта всегда основная) */
  mainCurrency: (item: any, _name: string) => {
    const Component = () => {
      const { mainCurrency } = useAuth(false);
      return <>{mainCurrency || 'RUB'}</>;
    };
    return <Component />;
  },
  foreignCurrency: (item: any, name: string) => {
    const Component = () => {
      const { mainCurrency } = useAuth(false);
      return item.currency !== mainCurrency ? (
        <>{`${item.currency} (${fCurrency(item[name], mainCurrency)})`}</>
      ) : (
        <></>
      );
    };
    return <Component />;
  },
  tags: (item: any, name: string) => {
    if (item[name]?.length > 0) {
      return (
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          flexWrap="wrap"
          sx={{
            '& > :not(style)': { m: 0.2 },
            gap: 0.2
          }}
        >
          {item[name].map(value => (
            <CopyableChip key={value} label={value} />
          ))}
        </Box>
      );
    }

    return '-';
  },
  shortTags: (item: any, name: string) => {
    let needShowItems = item[name];

    if (item[name]?.length > 0) {
      if (item[name].length > 1) {
        needShowItems = item[name].slice(0, 1);
      }
      return (
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          flexWrap="nowrap"
          sx={{
            '& > :not(style)': { m: 0.2 },
            gap: 0.2,
            overflow: 'visible'
          }}
        >
          {item[name].length === 1 ? (
            <CopyableChip label={needShowItems[0]} />
          ) : (
            <Box sx={{ position: 'relative' }}>
              <Badge
                badgeContent={`+${item[name].slice(1).length}`}
                overlap="circular"
                // color="primary" можно вернуть цвет
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  '& .MuiBadge-badge': {
                    width: 36
                  }
                }}
              />
              <CopyableChip label={needShowItems[0]} />
            </Box>
          )}
          {name === 'barcodes' && item?.hasBarcodeAsset && item?.id && (
            <Box sx={{ flexShrink: 0, display: 'inline-flex' }}>
              <BarcodeAssetChip productId={item.id} />
            </Box>
          )}
        </Box>
      );
    }

    // Файл этикетки (private key barcode) без строк в barcodes[]
    if (name === 'barcodes' && item?.hasBarcodeAsset && item?.id) {
      return (
        <Box
          display="flex"
          flexDirection="row"
          flexWrap="nowrap"
          alignItems="center"
          sx={{
            '& > :not(style)': { m: 0.2 },
            gap: 0.2,
            overflow: 'visible'
          }}
        >
          <Box sx={{ flexShrink: 0, display: 'inline-flex' }}>
            <BarcodeAssetChip productId={item.id} />
          </Box>
        </Box>
      );
    }

    return '-';
  },
  shortTag: (item: any, name: string) => {
    const raw = item?.[name];
    if (raw == null) return '-';
    const value = String(raw).trim();
    if (!value) return '-';
    return <CopyableChip label={value} />;
  },
  status: (item: any, name: string, params?: any) => {
    const statusName = item[name] || params?.defaultStatus;

    if (!statusName || (!STATUSES[statusName.toUpperCase()] && !params?.statuses?.[statusName.toUpperCase()])) {
      return '-';
    }

    const statuses = params?.statuses?.[statusName.toUpperCase()] || STATUSES[statusName.toUpperCase()];
    const Component = () => {
      const { t } = useI18n(false);
      const phrase = statusValueToI18nPhrase(String(statusName));

      return (
        <Label variant="soft" color={statuses?.color || 'default'} sx={{ textTransform: 'none' }}>
          {phrase ? t(phrase) : t(String(statusName))}
        </Label>
      );
    };

    return <Component />;
  },
  /** FBS/FBO в списках Ozon: как Label у status, uppercase. */
  ozonScheme: (item: any, name: string) => {
    const raw = item[name];
    if (raw == null || raw === '') return '-';
    const v = String(raw).trim().toLowerCase();
    const label =
      v === 'fbo' ? 'FBO' : v === 'fbw' ? 'FBW' : v === 'fbs' ? 'FBS' : String(raw).trim().toUpperCase();
    const color = v === 'fbo' || v === 'fbw' ? 'warning' : v === 'fbs' ? 'info' : 'default';
    return (
      <Label variant="soft" color={color} sx={{ textTransform: 'none' }}>
        {label}
      </Label>
    );
  },
  /** Статус заказа Avito (Order Management API) в списке постингов. */
  avitoPostingStatus: (item: any, name: string) => {
    const raw = item[name];
    if (raw == null || raw === '') return '—';
    const key = String(raw).trim();
    const color = getAvitoPostingStatusLabelColor(raw);
    const Component = () => {
      const { t } = useI18n(false);
      const translated = t(key);
      const text = translated === key ? formatAvitoPostingStatusLabel(raw) : translated;
      return (
        <Label variant="soft" color={color} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
          {text}
        </Label>
      );
    };
    return <Component />;
  },
  /** Схема доставки Avito: PVZ / DBS / RDBS. */
  avitoScheme: (item: any, name: string) => {
    const raw = item[name];
    if (raw == null || raw === '') return '-';
    const v = String(raw).trim().toLowerCase();
    const label =
      v === 'pvz' ? 'PVZ' : v === 'dbs' ? 'DBS' : v === 'rdbs' ? 'RDBS' : String(raw).trim().toUpperCase();
    const color = v === 'pvz' ? 'info' : v === 'dbs' ? 'warning' : v === 'rdbs' ? 'secondary' : 'default';
    return (
      <Label variant="soft" color={color} sx={{ textTransform: 'none' }}>
        {label}
      </Label>
    );
  },
  /** Статус отправления Ozon в ячейке скроллера: цветной текст (без Label), как у вкладок. */
  ozonPostingStatusText: (item: any, name: string) => {
    const raw = item[name];
    if (raw == null || raw === '') return '—';
    const formatted = formatOzonPostingStatusLabel(raw);
    return (
      <Box component="span" sx={{ color: getOzonPostingStatusSxColor(raw), fontWeight: 600 }}>
        {formatted}
      </Box>
    );
  },
  /** Налоговая база по чеку (список Ozon): Chip как на карточке отправления. */
  ozonReceiptTaxBaseChip: (item: any, name: string) => {
    const raw = getValueByPath(item, name);
    if (raw == null || raw === '' || Number(raw) === 0) return '—';
    const num = Number(raw);
    if (!Number.isFinite(num)) return '—';
    const cur = getValueByPath(item, 'taxBaseCustomerCurrencyCode');
    return (
      <Chip
        size="small"
        variant="outlined"
        color="success"
        label={fCurrencyByCode(num, typeof cur === 'string' ? cur : undefined)}
        sx={{ height: 20, '& .MuiChip-label': { px: 1, fontWeight: 600, fontSize: '0.7rem' } }}
      />
    );
  },
  /** Прибыль по заказу в ₽ (список Ozon). */
  ozonOrderProfitRub: (item: any, name: string) => {
    const raw = getValueByPath(item, name);
    if (raw == null || raw === '') return '—';
    const num = Number(raw);
    if (!Number.isFinite(num)) return '—';
    return (
      <Box component="span" sx={{ fontWeight: 600, color: num >= 0 ? 'success.main' : 'error.main' }}>
        {fCurrency(num, 'RUB')}
      </Box>
    );
  },
  /** Substatus Ozon в строке subname — только строка, стиль как у обычного subname (caption + text.disabled). */
  ozonPostingSubstatusPlain: (item: any, name: string) => {
    const raw = item[name];
    if (raw == null || raw === '') return '';
    return formatOzonPostingStatusLabel(raw);
  },
  /** Substatus Ozon — блок формы (Typography). */
  ozonPostingSubstatus: (item: any, name: string) => {
    const raw = item[name];
    if (raw == null || raw === '') return '—';
    return (
      <Typography variant="body2" component="span" sx={{ textTransform: 'none' }}>
        {formatOzonPostingStatusLabel(raw)}
      </Typography>
    );
  },
  translate: (item: any, name: string) => {
    if (!item[name]) {
      return '-';
    }

    const Component = () => {
      const { t } = useI18n(false);

      return <>{t(item[name])}</>;
    };

    return <Component />;
  },
  icon: (item: any, name: string, params?: { iconName?: string }) => {
    if (!item[name]) {
      return '';
    }

    if (!params?.iconName) {
      return item[name];
    }

    return <Iconify icon={params.iconName} />;
  },
  sync: (item: any, name: string, params?: { iconName?: string; labelKey?: string; tooltipLabelKey?: string }) => {
    if (!item[name] || item[name] === SYNC_STATUSES.none.value) {
      return '';
    }

    if (!params?.iconName) {
      return item[name];
    }

    const Component = () => (
      <Stack direction="row" gap={1}>
        <Label
          variant="soft"
          color={SYNC_STATUSES[item[name]]?.color || 'default'}
          sx={{ padding: '0px 5px', height: '20px' }}
        >
          <Iconify icon={params.iconName as string} boxSizing="content-box" width={16} />{' '}
        </Label>

        {params?.labelKey ? <Typography>{item[params.labelKey]}</Typography> : ''}
      </Stack>
    );

    if (!params?.tooltipLabelKey) {
      return <Component />;
    }

    return (
      <Tooltip title={item[params.tooltipLabelKey]} arrow placement="top-start">
        <Box>
          <Component />
        </Box>
      </Tooltip>
    );

    // return (
    //   <Box sx={{ padding: "5px" }}>
    //     <Badge
    //       variant="dot" color={SYNC_STATUSES[item[name]]?.color || 'default'}
    //       anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    //     >
    //       <Iconify icon={params.iconName} boxSizing="content-box" width={20}  />
    //     </Badge>
    //   </Box>
    // );
  },
  statusIcon: (
    item: any,
    name: string,
    params?: { iconName?: string; label?: string; statuses?: Record<string, any>; tooltipLabelKey?: string }
  ) => {
    const statusName = item[name];

    if (!statusName) return null;
    const statuses =
      params?.statuses?.[statusName.toUpperCase()] ||
      STATUSES[statusName.toUpperCase()] ||
      SYNC_STATUSES[statusName.toLowerCase()];
    const color = statuses?.color || 'default';
    const label = params?.label || name;
    const statusEntry = params?.statuses?.[statusName.toUpperCase()];
    const Component = () => {
      const { t } = useI18n(false);
      const tooltipPhrase = statusEntry?.phrase ? t(statusEntry.phrase) : t(statusName.toLowerCase());
      return (
        <Tooltip
          title={`${t(label)}: ${tooltipPhrase}${params?.tooltipLabelKey && item[params.tooltipLabelKey] ? ` (${item[params.tooltipLabelKey]})` : ''}`}
          arrow
          placement="top"
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Label
              variant="soft"
              color={color}
              sx={{ padding: '0px 5px', height: '20px', display: 'flex', alignItems: 'center' }}
            >
              {params?.iconName && <Iconify icon={params.iconName} boxSizing="content-box" width={16} />}
            </Label>
          </Stack>
        </Tooltip>
      );
    };
    return <Component />;
  },
  importanceNoteIcon: (item: any, name: string) => {
    const text = (item[name] ?? '').toString().trim();
    if (!text) return null;
    const Component = () => {
      const { t } = useI18n(false);
      return (
        <Tooltip title={text} arrow placement="top" enterDelay={400}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'error.main' }}>
            <Iconify icon="eva:alert-triangle-fill" width={20} />
          </Box>
        </Tooltip>
      );
    };
    return <Component />;
  },
  move: (item: any, name: string) => {
    const moveIn = typeof item[`${name}In`] === 'object' ? item[`${name}In`]?.name : item[`${name}In`];
    const moveOut = typeof item[`${name}Out`] === 'object' ? item[`${name}Out`]?.name : item[`${name}Out`];

    if (!moveIn) {
      return (
        <Box display="flex" alignItems="center">
          <Iconify icon="mdi:package-variant-minus" sx={{ mr: 1, color: 'error.main' }} />
          {moveOut}
        </Box>
      );
    }

    if (!moveOut) {
      return (
        <Box display="flex" alignItems="center">
          <Iconify icon="mdi:package-variant-closed-plus" sx={{ mr: 1, color: 'success.main' }} />
          {moveIn}
        </Box>
      );
    }

    return (
      <Box display="flex" alignItems="center">
        <Iconify icon="eva:repeat-fill" sx={{ mr: 1, color: 'warning.main' }} />
        <Box display="flex" flexDirection="column" alignItems="start">
          <div>{moveOut}</div>
          <div>{moveIn}</div>
        </Box>
      </Box>
    );
  },
  moveMoney: (item: any, name: string) => {
    const moveIn = typeof item[`${name}In`] === 'object' ? item[`${name}In`]?.name : item[`${name}In`];
    const moveOut = typeof item[`${name}Out`] === 'object' ? item[`${name}Out`]?.name : item[`${name}Out`];

    if (!moveIn) {
      return (
        <Box display="flex" alignItems="center">
          <Iconify icon="majesticons:money-minus-line" sx={{ mr: 1, color: 'error.main' }} />
          {moveOut}
        </Box>
      );
    }

    if (!moveOut) {
      return (
        <Box display="flex" alignItems="center">
          <Iconify icon="majesticons:money-plus-line" sx={{ mr: 1, color: 'success.main' }} />
          {moveIn}
        </Box>
      );
    }

    return (
      <Box display="flex" alignItems="center">
        <Iconify icon="eva:repeat-fill" sx={{ mr: 1, color: 'warning.main' }} />
        <Box display="flex" flexDirection="column" alignItems="start">
          <div>{moveOut}</div>
          <div>{moveIn}</div>
        </Box>
      </Box>
    );
  }
};

export const getFormattedValue = (item: any, name: string, format?: Formats, params?: { [key: string]: any }) => {
  const checkFormat = typeof format == 'string' ? formats[format] : format;
  return checkFormat ? checkFormat(item, name, params) : getValueByPath(item, name);
};

type CurrencyCode = 'RUB' | 'USD' | 'EUR' | 'CNY';

/**
 * Строковое представление значения для tooltip — с тем же форматированием, что и в ячейке.
 * Для форматов, которые возвращают React-элемент (money, currency и т.д.), нужен mainCurrency.
 */
export const getFormattedValueAsString = (
  item: any,
  name: string,
  format?: Formats,
  params?: { [key: string]: any; currency?: CurrencyCode; digits?: number; nullable?: boolean },
  mainCurrency?: CurrencyCode
): string => {
  const base = mainCurrency || 'RUB';
  const formatName = typeof format === 'string' ? format : undefined;

  if (formatName === 'money') {
    const cur = (params?.currency ?? item?.currency ?? base ?? 'RUB') as CurrencyCode;
    const raw = getValueByPath(item, name);
    const value = fCurrency(raw, cur, params?.digits);
    if (params?.nullable === false && (raw == null || raw === '')) return '';
    return value || '-';
  }
  if (formatName === 'moneyRUB') {
    const raw = getValueByPath(item, name);
    const value = fCurrency(raw, base, params?.digits);
    if (params?.nullable === false && (raw == null || raw === '')) return '';
    return (value as string) || '-';
  }
  if (formatName === 'moneyWithRUB') {
    const primaryCurrency = item?.currency || base;
    const raw = getValueByPath(item, name);
    const primary = fCurrency(raw, primaryCurrency);
    const showSecond = primaryCurrency !== base;
    const second = showSecond ? ` (${fCurrency(item[`${name}Rub`], base)})` : '';
    return `${primary}${second}`;
  }
  if (formatName === 'currency') {
    const curr = item?.currency || base;
    const showBase = curr !== base;
    return showBase ? `${curr} (${fCurrency(getValueByPath(item, name), base)})` : (curr || '-');
  }
  if (formatName === 'foreignCurrency') {
    return item.currency !== mainCurrency
      ? `${item.currency} (${fCurrency(getValueByPath(item, name), mainCurrency)})`
      : '';
  }
  if (formatName === 'linkedAmount') {
    const raw = getValueByPath(item, name);
    const value = fCurrency(raw, item.currency || 'RUB');
    if (!raw) return '';
    return value;
  }
  if (formatName === 'ozonReceiptTaxBaseChip') {
    const raw = getValueByPath(item, name);
    if (raw == null || raw === '') return '—';
    if (Number(raw) === 0) return '—';
    const num = Number(raw);
    if (!Number.isFinite(num)) return '—';
    const cur = getValueByPath(item, 'taxBaseCustomerCurrencyCode');
    return fCurrencyByCode(num, typeof cur === 'string' ? cur : undefined) as string;
  }
  if (formatName === 'ozonOrderProfitRub') {
    const raw = getValueByPath(item, name);
    if (raw == null || raw === '') return '—';
    const num = Number(raw);
    if (!Number.isFinite(num)) return '—';
    return fCurrency(num, base) as string;
  }
  if (formatName === 'ozonScheme') {
    const raw = getValueByPath(item, name);
    if (raw == null || raw === '') return '-';
    const v = String(raw).trim().toLowerCase();
    return v === 'fbo' ? 'FBO' : v === 'fbw' ? 'FBW' : v === 'fbs' ? 'FBS' : String(raw).trim().toUpperCase();
  }
  if (formatName === 'avitoScheme') {
    const raw = getValueByPath(item, name);
    if (raw == null || raw === '') return '-';
    const v = String(raw).trim().toLowerCase();
    return v === 'pvz' ? 'PVZ' : v === 'dbs' ? 'DBS' : v === 'rdbs' ? 'RDBS' : String(raw).trim().toUpperCase();
  }
  if (formatName === 'avitoPostingStatus') {
    const raw = getValueByPath(item, name);
    return formatAvitoPostingStatusLabel(raw);
  }
  if (formatName === 'ozonPostingStatusText') {
    const raw = getValueByPath(item, name);
    if (raw == null || raw === '') return '—';
    return formatOzonPostingStatusLabel(raw) || '—';
  }
  if (formatName === 'ozonPostingSubstatusPlain' || formatName === 'ozonPostingSubstatus') {
    const raw = getValueByPath(item, name);
    if (raw == null || raw === '') return '—';
    return formatOzonPostingStatusLabel(raw) || '—';
  }
  /** shortTags/tags рендерят React — иначе tooltip получал «-» из fallback по объекту */
  if (formatName === 'shortTags' || formatName === 'tags') {
    const arr = getValueByPath(item, name);
    const parts: string[] = [];
    if (Array.isArray(arr) && arr.length > 0) {
      parts.push(arr.map((v) => String(v)).join(', '));
    }
    return parts.length > 0 ? parts.join(' · ') : '-';
  }
  if (formatName === 'status') {
    const statusName = getValueByPath(item, name) ?? params?.defaultStatus;
    if (!statusName || (!STATUSES[String(statusName).toUpperCase()] && !params?.statuses?.[String(statusName).toUpperCase()])) {
      return '-';
    }
    const phrase = statusValueToI18nPhrase(String(statusName));
    return phrase ? t(phrase) : t(String(statusName));
  }

  const result = getFormattedValue(item, name, format, params);
  if (result == null || result === '') return '-';
  if (typeof result === 'string') return result;
  if (typeof result === 'number') return String(result);
  if (result instanceof Date) return fDate(result);
  return '-';
};

/** Filter nav items by role and permissions. ADMIN sees all; others see only items they have permission for. */
export const filterPermission = (role: string, permissions: string[] = []) => {
  const hasPermission = (permission?: string) => {
    if (!permission) return true; // no permission required — always show (e.g. Main)
    if (role === ROLES.ADMIN) return true;
    return permissions.includes(permission);
  };

  const filterItem = (item: any): any => {
    if (!hasPermission(item.permission)) return null;
    if (item.children) {
      const filteredChildren = item.children.map(filterItem).filter(Boolean);
      if (filteredChildren.length === 0) return null;
      return { ...item, children: filteredChildren };
    }
    return item;
  };

  return navConfig
    .map((config) => ({
      ...config,
      items: config.items.map(filterItem).filter(Boolean),
    }))
    .filter((config) => config.items.length > 0);
};

// CopyableChip компонент для копирования по клику/тапу с подсветкой
export function CopyableChip({ label, size }: { label: string, size?: 'small' | 'medium' }) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  let timer: NodeJS.Timeout | null = null;

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(label);
      setCopied(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Chip
      label={label}
      variant="soft"
      onClick={handleCopy}
      onTouchEnd={handleCopy}
      size={size}
      sx={{
        cursor: 'pointer',
        bgcolor: copied ? theme.palette.success.main : undefined,
        color: copied ? theme.palette.success.contrastText : undefined,
        transition: 'background 0.2s, color 0.2s',
      }}
    />
  );
}
